import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, getToken, clearToken } from '../api';
import { SECTION_LABELS, SECTION_ROUTES } from '../components/ProfilePrompt';
import LocationSearch from '../components/LocationSearch';

export default function Settings() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [completeness, setCompleteness] = useState(null);
  const [isPlus, setIsPlus] = useState(false);
  const [travelLocation, setTravelLocation] = useState('');
  const [dreamLocation, setDreamLocation] = useState('');
  const [savingTravel, setSavingTravel] = useState(false);
  const [savingDream, setSavingDream] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeCode, setUpgradeCode] = useState('');
  const [upgradeError, setUpgradeError] = useState('');
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    if (!getToken()) { navigate('/login'); return; }
    api.get('/profile').then(p => {
      setProfile(p);
      setIsPlus(p.subscription_tier === 'plus');
      setTravelLocation(p.travel_location || '');
      setDreamLocation(p.dream_location || '');
    }).catch(() => {});
    api.get('/profile/completeness').then(setCompleteness).catch(() => {});
  }, [navigate]);

  const saveLocation = async (type, location, lat, lng) => {
    const setSaving = type === 'travel' ? setSavingTravel : setSavingDream;
    setSaving(true);
    try {
      await api.post('/profile/preferences', {
        [`${type}_location`]: location,
        [`${type}_lat`]: lat,
        [`${type}_lng`]: lng,
      });
      if (type === 'travel') setTravelLocation(location);
      else setDreamLocation(location);
    } finally {
      setSaving(false);
    }
  };

  const submitUpgradeCode = async () => {
    if (!upgradeCode.trim()) return;
    setUpgrading(true);
    setUpgradeError('');
    try {
      await api.post('/upgrade', { code: upgradeCode.trim() });
      setIsPlus(true);
      setShowUpgrade(false);
      setUpgradeCode('');
    } catch {
      setUpgradeError('Invalid code — try again.');
    } finally {
      setUpgrading(false);
    }
  };

  const handleSignOut = () => {
    clearToken();
    navigate('/');
  };

  const togglePaused = async () => {
    const next = !profile?.matching_paused;
    setProfile(p => ({ ...p, matching_paused: next }));
    try {
      await api.post('/profile/preferences', { matching_paused: next });
    } catch {
      setProfile(p => ({ ...p, matching_paused: !next }));
    }
  };

  return (
    <div className="settings-screen">
      <div className="display-sm" style={{ marginBottom: 40 }}>Settings</div>

      {/* Profile strength */}
      {completeness && (
        <div className="profile-strength-section">
          <div className="settings-section-label">Profile strength</div>
          <div className="profile-strength-bar">
            <div className="profile-strength-fill" style={{ width: `${completeness.overall_score}%` }} />
          </div>
          <div style={{ fontSize: 12, color: 'var(--very-muted)', fontWeight: 300, lineHeight: 1.6 }}>
            Your matches improve as your profile grows
          </div>
          {completeness.missing_sections.length > 0 && (
            <div className="profile-strength-missing">
              {completeness.missing_sections.map(section => {
                const route = SECTION_ROUTES[section];
                return (
                  <div
                    key={section}
                    className="profile-strength-item"
                    onClick={() => route && navigate(route)}
                    style={{ cursor: route ? 'pointer' : 'default' }}
                  >
                    <span className="profile-strength-item-label">{SECTION_LABELS[section] || section}</span>
                    {route && <span className="profile-strength-item-action">Add →</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="settings-section">
        <div className="settings-section-label">Your profile</div>

        {profile && (
          <div className="settings-row" onClick={() => navigate('/onboarding/profile', { state: { fromSettings: true } })}>
            <div>
              <div className="settings-row-label">{profile.name || 'Complete your profile'}</div>
              {profile.location && (
                <div className="settings-row-value" style={{ marginTop: 4 }}>{profile.location}</div>
              )}
            </div>
            <div className="settings-row-value">Edit →</div>
          </div>
        )}

        <div className="settings-row" onClick={() => navigate('/settings/photos')}>
          <span className="settings-row-label">Photos</span>
          <span className="settings-row-value">Edit →</span>
        </div>

        <div className="settings-row" onClick={() => navigate('/onboarding/birth', { state: { fromSettings: true } })}>
          <span className="settings-row-label">Birth data</span>
          <span className="settings-row-value">Edit →</span>
        </div>

        <div className="settings-row" onClick={() => navigate('/onboarding/path')}>
          <span className="settings-row-label">Retake questionnaire</span>
          <span className="settings-row-value">→</span>
        </div>

        <div className="settings-row" onClick={() => navigate('/reset-swipe-test')} style={{ cursor: 'pointer' }}>
          <div>
            <div className="settings-row-label">Retake attraction test</div>
            <div className="settings-row-value" style={{ marginTop: 4, fontSize: 12 }}>Redo the photo swipe if results feel off</div>
          </div>
          <span className="settings-row-value">→</span>
        </div>
      </div>

      <div className="settings-section">
        <div className="settings-section-label">Tools</div>
        <div className="settings-row" onClick={() => navigate('/partner-test')} style={{ cursor: 'pointer' }}>
          <div>
            <div className="settings-row-label">Test My Partner</div>
            <div className="settings-row-value" style={{ marginTop: 4, fontSize: 12 }}>Photo + birth date → compatibility snapshot</div>
          </div>
          <span className="settings-row-value">→</span>
        </div>
        <div className="settings-row" onClick={() => navigate('/settings/partners')} style={{ cursor: 'pointer' }}>
          <div>
            <div className="settings-row-label">Uploaded partners</div>
            <div className="settings-row-value" style={{ marginTop: 4, fontSize: 12 }}>Your saved compatibility tests</div>
          </div>
          <span className="settings-row-value">→</span>
        </div>
      </div>

      <div className="settings-section">
        <div className="settings-section-label">Discovery</div>
        <div className="settings-row" style={{ cursor: 'pointer' }} onClick={togglePaused}>
          <div>
            <div className="settings-row-label">Hide my profile</div>
            <div className="settings-row-value" style={{ marginTop: 4, fontSize: 12 }}>
              {profile?.matching_paused
                ? 'Your profile is hidden — you won\'t appear in new matches'
                : 'You\'re visible and getting new matches'}
            </div>
          </div>
          <div style={{
            width: 44, height: 26, borderRadius: 13, flexShrink: 0,
            background: profile?.matching_paused ? '#2a2a2a' : 'rgba(201,168,76,0.25)',
            border: profile?.matching_paused ? '0.5px solid #333' : '0.5px solid rgba(201,168,76,0.5)',
            position: 'relative', transition: 'all 0.2s',
          }}>
            <div style={{
              width: 20, height: 20, borderRadius: '50%',
              background: profile?.matching_paused ? '#444' : 'var(--gold)',
              position: 'absolute', top: 3,
              left: profile?.matching_paused ? 3 : 21,
              transition: 'left 0.2s, background 0.2s',
            }} />
          </div>
        </div>
      </div>

      <div className="settings-section">
        <div className="settings-section-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Matchmaker+</span>
          <span style={{
            fontSize: 10, letterSpacing: 1,
            color: isPlus ? 'var(--gold)' : 'var(--very-muted)',
            border: `0.5px solid ${isPlus ? 'rgba(201,168,76,0.4)' : 'rgba(255,255,255,0.1)'}`,
            padding: '2px 8px', borderRadius: 2,
          }}>
            {isPlus ? 'ACTIVE' : 'FREE'}
          </span>
        </div>

        {/* Voice chat */}
        <div className="settings-row" style={{ opacity: isPlus ? 1 : 0.45 }}>
          <div>
            <div className="settings-row-label">Voice chat with Paula</div>
            <div className="settings-row-value" style={{ marginTop: 4, fontSize: 12 }}>
              {isPlus ? 'Hold the mic to talk' : 'Upgrade to unlock'}
            </div>
          </div>
          <span style={{ fontSize: 16 }}>{isPlus ? '🎙' : '🔒'}</span>
        </div>

        {/* Message requests */}
        <div
          className="settings-row"
          style={{ cursor: 'pointer' }}
          onClick={() => navigate('/settings/requests')}
        >
          <div>
            <div className="settings-row-label">Message requests</div>
            <div className="settings-row-value" style={{ marginTop: 4, fontSize: 12 }}>
              When someone reaches out before matching
            </div>
          </div>
          <span className="settings-row-value">→</span>
        </div>

        {/* 3-Location mode */}
        <div style={{ opacity: isPlus ? 1 : 0.45 }}>
          <div style={{ padding: '14px 0 8px', borderBottom: '0.5px solid rgba(255,255,255,0.06)' }}>
            <div className="settings-row-label" style={{ marginBottom: 4 }}>3-Location mode</div>
            <div className="settings-row-value" style={{ fontSize: 12, marginBottom: isPlus ? 14 : 0 }}>
              {isPlus ? 'Show your profile in up to 3 locations at once' : 'Upgrade to unlock'}
            </div>
            {isPlus && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--very-muted)', letterSpacing: 1, marginBottom: 4 }}>HOME</div>
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', padding: '8px 0' }}>
                    {profile?.location || '—'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--very-muted)', letterSpacing: 1, marginBottom: 4 }}>TRAVEL</div>
                  <LocationSearch
                    placeholder={travelLocation || 'Add travel location'}
                    initialValue={travelLocation}
                    key={`travel-${profile?.travel_location}`}
                    onSelect={({ display, lat, lng }) => saveLocation('travel', display, lat, lng)}
                  />
                  {savingTravel && <div style={{ fontSize: 11, color: 'var(--very-muted)', marginTop: 4 }}>Saving…</div>}
                </div>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--very-muted)', letterSpacing: 1, marginBottom: 4 }}>DREAM</div>
                  <LocationSearch
                    placeholder={dreamLocation || 'Add dream location'}
                    initialValue={dreamLocation}
                    key={`dream-${profile?.dream_location}`}
                    onSelect={({ display, lat, lng }) => saveLocation('dream', display, lat, lng)}
                  />
                  {savingDream && <div style={{ fontSize: 11, color: 'var(--very-muted)', marginTop: 4 }}>Saving…</div>}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Open to distance dating */}
        <div className="settings-row" style={{ cursor: 'pointer' }} onClick={async () => {
          if (!isPlus) return;
          const next = !profile?.open_to_distance_dating;
          setProfile(p => ({ ...p, open_to_distance_dating: next }));
          try {
            await api.post('/profile/preferences', { open_to_distance_dating: next });
          } catch {
            setProfile(p => ({ ...p, open_to_distance_dating: !next }));
          }
        }}>
          <div style={{ opacity: isPlus ? 1 : 0.45 }}>
            <div className="settings-row-label">Open to distance dating</div>
            <div className="settings-row-value" style={{ marginTop: 4, fontSize: 12 }}>
              {isPlus
                ? 'Appear to Matchmaker+ users browsing your location'
                : 'Upgrade to unlock'}
            </div>
          </div>
          {isPlus && (
            <div style={{
              width: 44, height: 26, borderRadius: 13, flexShrink: 0,
              background: profile?.open_to_distance_dating ? 'rgba(201,168,76,0.25)' : '#2a2a2a',
              border: profile?.open_to_distance_dating ? '0.5px solid rgba(201,168,76,0.5)' : '0.5px solid #333',
              position: 'relative', transition: 'all 0.2s',
            }}>
              <div style={{
                width: 20, height: 20, borderRadius: '50%',
                background: profile?.open_to_distance_dating ? 'var(--gold)' : '#444',
                position: 'absolute', top: 3,
                left: profile?.open_to_distance_dating ? 21 : 3,
                transition: 'left 0.2s, background 0.2s',
              }} />
            </div>
          )}
        </div>

        {!isPlus && (
          <div
            onClick={() => setShowUpgrade(true)}
            style={{
              margin: '4px 0 8px',
              padding: '12px 16px',
              background: 'rgba(201,168,76,0.06)',
              border: '0.5px solid rgba(201,168,76,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              cursor: 'pointer',
            }}
          >
            <div>
              <div style={{ fontSize: 13, color: 'var(--gold)', fontWeight: 400 }}>Unlock Matchmaker+</div>
              <div style={{ fontSize: 11, color: 'var(--very-muted)', marginTop: 2 }}>Voice · 3 locations · Message first</div>
            </div>
            <span style={{ fontSize: 13, color: 'var(--gold)' }}>$25 / mo →</span>
          </div>
        )}
      </div>

      <div className="settings-section">
        <div className="settings-section-label">Subscription</div>

        <div className="settings-row">
          <span className="settings-row-label">Current plan</span>
          <span className="settings-row-value">{isPlus ? 'Matchmaker+' : 'Free'}</span>
        </div>

        {!isPlus && (
          <div className="settings-row" style={{ cursor: 'pointer' }} onClick={() => setShowUpgrade(true)}>
            <span className="settings-row-label" style={{ color: 'var(--gold)' }}>Unlock all matches</span>
            <span className="settings-row-value" style={{ color: 'var(--gold)' }}>$25 / mo →</span>
          </div>
        )}
      </div>

      <div className="settings-section">
        <div className="settings-section-label">Account</div>

        <div className="settings-row">
          <span className="settings-row-label">Notifications</span>
          <span className="settings-row-value">Weekly</span>
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        <button className="settings-sign-out" onClick={handleSignOut}>
          Sign out
        </button>
      </div>

      <div style={{ marginTop: 48, opacity: 0.25 }}>
        <div className="settings-row" onClick={() => navigate('/reset-swipe-test')} style={{ cursor: 'pointer' }}>
          <span className="settings-row-label" style={{ fontSize: 11, letterSpacing: 1 }}>Reset swipe test</span>
          <span className="settings-row-value">↺</span>
        </div>
      </div>

      {showUpgrade && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 200,
          background: 'rgba(0,0,0,0.92)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 32,
        }}>
          <div style={{
            width: '100%', maxWidth: 340,
            background: '#0d0d0d', border: '0.5px solid rgba(201,168,76,0.25)',
            padding: 28,
          }}>
            <div style={{ fontSize: 11, letterSpacing: 2, color: 'var(--gold)', marginBottom: 16 }}>UPGRADE TO MATCHMAKER+</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 300, lineHeight: 1.6, marginBottom: 24 }}>
              Enter your access code to unlock voice chat, 3-location mode, and message before matching.
            </div>
            <input
              autoFocus
              value={upgradeCode}
              onChange={e => { setUpgradeCode(e.target.value); setUpgradeError(''); }}
              onKeyDown={e => e.key === 'Enter' && submitUpgradeCode()}
              placeholder="Access code"
              style={{
                width: '100%', background: 'rgba(255,255,255,0.04)',
                border: '0.5px solid rgba(255,255,255,0.12)',
                color: 'var(--text)', fontFamily: 'var(--font-ui)',
                fontSize: 14, fontWeight: 300, padding: '11px 14px',
                outline: 'none', boxSizing: 'border-box', marginBottom: 8,
              }}
            />
            {upgradeError && (
              <div style={{ fontSize: 12, color: 'rgba(255,80,60,0.7)', marginBottom: 12 }}>{upgradeError}</div>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button
                onClick={() => { setShowUpgrade(false); setUpgradeCode(''); setUpgradeError(''); }}
                style={{
                  flex: 1, padding: '10px 0', background: 'transparent',
                  border: '0.5px solid rgba(255,255,255,0.1)',
                  color: 'var(--very-muted)', fontSize: 13, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitUpgradeCode}
                disabled={!upgradeCode.trim() || upgrading}
                style={{
                  flex: 1, padding: '10px 0',
                  background: upgradeCode.trim() ? 'rgba(201,168,76,0.12)' : 'transparent',
                  border: `0.5px solid ${upgradeCode.trim() ? 'rgba(201,168,76,0.5)' : 'rgba(255,255,255,0.1)'}`,
                  color: upgradeCode.trim() ? 'var(--gold)' : 'var(--very-muted)',
                  fontSize: 13, cursor: upgradeCode.trim() ? 'pointer' : 'default',
                }}
              >
                {upgrading ? 'Checking...' : 'Unlock'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
